import React, { useEffect, useState, useMemo, useRef } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import {
    AiOutlineClose,
    AiOutlineSearch,
    AiOutlineEdit,
    AiOutlineEye,
} from "react-icons/ai";
import SummaryApi from "../common";
import { useTable, usePagination } from "react-table";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFileExcel, FaUserGraduate, FaFilter, FaSlidersH } from "react-icons/fa";
import EnrolledStudentsTable from "../components/EnrolledStudentsTable";
import EnrolledStudentsModal from "../components/EnrolledStudentsModal";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

Modal.setAppElement("#root");

const THEME = {
    primary: "#172C57",
    primaryLight: "rgba(23, 44, 87, 0.08)",
    primaryHover: "#1E3A6E",
    emerald: "#10B981",
    purple: "#7C3AED",
    red: "#EF4444",
    amber: "#F59E0B",
    blue: "#2563EB",
};

const parseUrlFilter = (urlFilter) => {
    if (!urlFilter) return { targetStatus: "All", isToday: false };

    const filterKey = urlFilter.toLowerCase();

    const isToday =
        filterKey === "today" ||
        filterKey.endsWith("_today") ||
        filterKey.includes("today");

    let targetStatus = "All";
    if (filterKey.startsWith("most_interested")) {
        targetStatus = "Most Interested";
    } else if (filterKey.startsWith("not_interested")) {
        targetStatus = "Not Interested";
    } else if (filterKey.startsWith("interested")) {
        targetStatus = "Interested";
    } else if (filterKey.startsWith("not_answered")) {
        targetStatus = "Not Answered";
    }

    return { targetStatus, isToday };
};

// Stable identity key for a student/lead row. Used to dedupe search results.
const makeStudentKey = (s) => {
    const sid = String(s?.student_id ?? "").trim();
    const eid = String(s?.employee_student_id ?? "").trim();
    if (sid || eid) return `${sid}|${eid}`;
    return `mobile:${String(s?.mobile_number1 ?? "").trim()}`;
};

const EnrolledStudents = () => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [studentsData, setStudentsData] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [isUpdate, setIsUpdate] = useState(false);
    const [isUpload, setIsUpload] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchInputRef = useRef(null);
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [dropdownValue, setDropdownValue] = useState("");
    const [minEnrolledDate, setMinEnrolledDate] = useState(null);
    const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());
    const [totalStudents, setTotalStudents] = useState(0);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [filterDate, setFilterDate] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [activeSearchTerm, setActiveSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [memberType, setMemberType] = useState(null);
    const [localPageIndex, setLocalPageIndex] = useState(0);
    const [updatedData, setUpdatedData] = useState({});
    const [updatedStudent, setUpdatedStudent] = useState(0);
    const [timer, setTimer] = useState("00:00:00");
    const [intervalId, setIntervalId] = useState(null);
    const [canOnlySelectHigherInterest, setCanOnlySelectHigherInterest] =
        useState(false);
    const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
    const [searchDistrict, setSearchDistrict] = useState("");
    const [searchCity, setSearchCity] = useState("");
    const [searchCollege, setSearchCollege] = useState("");
    const [exporting, setExporting] = useState(false);

    const [formData, setFormData] = useState({
        student_name: "",
        gender: "Male",
        father_name: "",
        mobile_number1: "",
        mobile_number2: "",
        email: "",
        aadhar_number: "",
        state: "",
        district: "",
        city: "",
        address: "",
        pincode: "",
        course: "",
        clg_name: "",
        program_type: "",
        ug_pg_course: "",
        call_back_reason: "",
        call_duration: "00:00:00.000000",
        other_reasons: "",
        is_interested: "",
        call_status: "",
        remarks: "",
        prev_call_back_reason: "",
        follow_up_date: "",
        next_follow_up_date: "",
        previous_status: "",
        inter_percentage: "",
        is_jee: "",
        is_jee2: "",
        hours: "00",
        minutes: "00",
        seconds: "00",
        lead_type: "",
        native_district: "",
        native_address: "",
    });

    const districts = useMemo(
        () => [
            "Alluri Sitharama Raju",
            "Anakapalli",
            "Ananthapuram",
            "Annamayya",
            "Bapatla",
            "CHITTOOR",
            "EAST GODAVARI",
            "Eluru",
            "Guntur",
            "Kakinada",
            "Konaseema",
            "Krishna",
            "Kurnool",
            "Manyam",
            "Nandyal",
            "NTR",
            "Palnadu",
            "Prakasam",
            "Srikakulam",
            "SPS Nellore",
            "Sri Satyasai",
            "Tirupathi",
            "Vizianagaram",
            "Visakhapatnam",
            "West Godavari",
            "YSR Kadapa",
        ],
        []
    );

    const headers = {
        Authorization: "Bearer sandeep_university_916",
        "Content-Type": "application/json",
    };

    const gotoPageRef = useRef(null);

    const studentsDataRef = useRef([]);
    const memberTypeRef = useRef(null);
    const filteredStudentsRef = useRef([]);

    useEffect(() => {
        studentsDataRef.current = studentsData;
    }, [studentsData]);

    useEffect(() => {
        memberTypeRef.current = memberType;
    }, [memberType]);

    useEffect(() => {
        filteredStudentsRef.current = filteredStudents;
    }, [filteredStudents]);

    useEffect(() => {
        const today = new Date();
        const urlParams = new URLSearchParams(window.location.search);
        const urlFilter = urlParams.get("filter");

        const { targetStatus, isToday } = parseUrlFilter(urlFilter);

        if (targetStatus !== "All") {
            setSelectedFilter(targetStatus);
        }

        if (isToday) {
            setFromDate(today);
            setToDate(today);
        } else {
            const firstDayLastMonth = new Date(
                today.getFullYear(),
                today.getMonth() - 1,
                1
            );
            setFromDate(firstDayLastMonth);
            setToDate(today);
        }
    }, []);

    useEffect(() => {
        const userRaw = sessionStorage.getItem("user");
        const user = JSON.parse(userRaw || "{}");
        const memberType = user?.member_type;
        setMemberType(memberType);
    }, []);

    useEffect(() => {
        if (modalIsOpen && (isUpdate || isUpload)) {
            let seconds = 0;
            const id = setInterval(() => {
                seconds++;
                const hours = String(Math.floor(seconds / 3600)).padStart(2, "0");
                const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(
                    2,
                    "0"
                );
                const secs = String(seconds % 60).padStart(2, "0");
                setTimer(`${hours}:${minutes}:${secs}`);
            }, 1000);
            setIntervalId(id);
            return () => clearInterval(id);
        } else {
            if (intervalId) clearInterval(intervalId);
            setTimer("00:00:00");
        }
    }, [modalIsOpen, isUpdate, isUpload]);

    const fetchStudents = async (skipLoading = false) => {
        if (!skipLoading) setPageLoading(true);
        try {
            const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
            const type = encodedMemberType ? atob(encodedMemberType) : null;

            if (!type) {
                setStudentsData([]);
                setFilteredStudents([]);
                setTotalStudents(0);
                setPageLoading(false);
                return;
            }

            setMemberType(type);
            memberTypeRef.current = type;

            if (!fromDate || !toDate) return;

            let response;

            if (type === "Admin" || type === "Manager") {
                response = await fetch(SummaryApi.admindatepicker.url, {
                    method: SummaryApi.admindatepicker.method,
                    headers: headers,
                    body: JSON.stringify({
                        start_date: fromDate.toISOString().split("T")[0],
                        end_date: toDate.toISOString().split("T")[0],
                    }),
                });
            } else if (type === "Employee") {
                const encodedEmployeeId = sessionStorage.getItem(
                    "H7Z4L9VD2XK5JW8QRYT6"
                );
                if (!encodedEmployeeId) throw new Error("Employee ID not found");
                const decodedEmployeeId = atob(encodedEmployeeId);
                response = await fetch(SummaryApi.studentsretreivebyemployeeid.url, {
                    method: SummaryApi.studentsretreivebyemployeeid.method,
                    headers: headers,
                    body: JSON.stringify({
                        employee_id: decodedEmployeeId,
                        start_date: fromDate.toISOString().split("T")[0],
                        end_date: toDate.toISOString().split("T")[0],
                    }),
                });
            } else {
                const encodedEmployeeId = sessionStorage.getItem(
                    "H7Z4L9VD2XK5JW8QRYT6"
                );
                if (encodedEmployeeId) {
                    const decodedEmployeeId = atob(encodedEmployeeId);
                    response = await fetch(SummaryApi.studentsretreivebyemployeeid.url, {
                        method: SummaryApi.studentsretreivebyemployeeid.method,
                        headers: headers,
                        body: JSON.stringify({
                            employee_id: decodedEmployeeId,
                            start_date: fromDate.toISOString().split("T")[0],
                            end_date: toDate.toISOString().split("T")[0],
                        }),
                    });
                } else {
                    setStudentsData([]);
                    setFilteredStudents([]);
                    setTotalStudents(0);
                    setPageLoading(false);
                    return;
                }
            }

            if (!response?.ok) throw new Error("Failed to fetch data");

            const data = await response.json();
            const students = data?.data || [];
            const updatedStudentsData = students.map((student, index) => ({
                ...student,
                sno: index + 1,
                call_duration: student.call_duration
                    ? student.call_duration.replace(/\.\d{6}$/, "")
                    : student.call_duration,
            }));

            setTotalStudents(data?.total_students || students.length);
            setStudentsData(updatedStudentsData);

            const urlParams = new URLSearchParams(window.location.search);
            const { targetStatus } = parseUrlFilter(urlParams.get("filter"));
            const activeFilter = targetStatus !== "All" ? targetStatus : selectedFilter;

            if (activeFilter !== "All") {
                let preFiltered = updatedStudentsData;
                if (activeFilter === "Not Answered") {
                    preFiltered = updatedStudentsData.filter(
                        (student) => student?.call_status === activeFilter
                    );
                } else {
                    preFiltered = updatedStudentsData.filter(
                        (student) => student?.is_interested === activeFilter
                    );
                }
                setFilteredStudents(preFiltered);
            } else {
                setFilteredStudents(updatedStudentsData);
            }

            setActiveSearchTerm("");
            setSearchResults([]);
            setSearchQuery("");
            setShowSuggestions(false);

            if (gotoPageRef.current) gotoPageRef.current(0);
            setLocalPageIndex(0);
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error(`Error fetching students: ${error.message}`);
            setStudentsData([]);
            setFilteredStudents([]);
            setTotalStudents(0);
        } finally {
            setPageLoading(false);
        }
    };

    const resetSearchStates = () => {
        setActiveSearchTerm("");
        setSearchResults([]);
        setSearchQuery("");
        setShowSuggestions(false);
        setSearchSuggestions([]);
    };

    const debouncedSearch = useMemo(
        () =>
            debounce(async (query) => {
                const latestStudents = studentsDataRef.current;
                const latestMemberType = memberTypeRef.current;
                const trimmed = query.trim().toLowerCase();

                if (!trimmed || trimmed.length < 3) {
                    setFilteredStudents(latestStudents);
                    setTotalStudents(latestStudents.length);
                    resetSearchStates();
                    if (gotoPageRef.current) gotoPageRef.current(0);
                    return;
                }

                setIsSearching(true);
                setActiveSearchTerm(trimmed);

                const localMatches = latestStudents.filter((student) => {
                    const studentName = (student.student_name || "").toLowerCase();
                    const employeeName = (student.employee_name || "").toLowerCase();
                    const mobile = (student.mobile_number1 || "").toLowerCase();
                    const college = (student.clg_name || "").toLowerCase();
                    return (
                        studentName.includes(trimmed) ||
                        employeeName.includes(trimmed) ||
                        mobile.includes(trimmed) ||
                        college.includes(trimmed)
                    );
                });

                try {
                    let apiEndpoint = SummaryApi.adminsearchbar.url;
                    let body = { search_term: trimmed };

                    if (
                        latestMemberType === "Employee" ||
                        latestMemberType === "Digital Leads"
                    ) {
                        const encodedId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
                        if (encodedId) {
                            body.employee_id = atob(encodedId);
                            apiEndpoint = SummaryApi.employeesearchbar.url;
                        }
                    }

                    const res = await fetch(apiEndpoint, {
                        method: "POST",
                        headers,
                        body: JSON.stringify(body),
                    });

                    const data = await res.json();

                    let combined = [];
                    if (data?.status && Array.isArray(data.data)) {
                        // Dedupe the server payload itself AND normalize ids.
                        const serverSeen = new Set();
                        const serverProcessed = [];
                        data.data.forEach((s) => {
                            const key = makeStudentKey(s);
                            if (serverSeen.has(key)) return;
                            serverSeen.add(key);
                            serverProcessed.push({
                                ...s,
                                sno: serverProcessed.length + 1,
                                call_duration:
                                    s.call_duration?.replace(/\.\d{6}$/, "") ||
                                    "00:00:00",
                            });
                        });

                        // Merge server + local using the same composite key so
                        // a student present in both lists is only shown once.
                        combined = [...serverProcessed];
                        serverProcessed.forEach((s) => {
                            serverSeen.add(makeStudentKey(s));
                        });

                        localMatches.forEach((s) => {
                            const key = makeStudentKey(s);
                            if (!serverSeen.has(key)) {
                                serverSeen.add(key);
                                combined.push(s);
                            }
                        });
                    } else {
                        // Local results only — dedupe to be safe.
                        const localSeen = new Set();
                        combined = localMatches.filter((s) => {
                            const key = makeStudentKey(s);
                            if (localSeen.has(key)) return false;
                            localSeen.add(key);
                            return true;
                        });
                    }

                    const reindexed = combined.map((s, i) => ({ ...s, sno: i + 1 }));
                    setFilteredStudents(reindexed);
                    setTotalStudents(reindexed.length);

                    if (gotoPageRef.current) gotoPageRef.current(0);
                    setLocalPageIndex(0);
                } catch (err) {
                    console.error("Search API failed, using client-side match:", err);
                    // Dedupe local matches before applying.
                    const localSeen = new Set();
                    const dedupedLocal = localMatches.filter((s) => {
                        const key = makeStudentKey(s);
                        if (localSeen.has(key)) return false;
                        localSeen.add(key);
                        return true;
                    });
                    setFilteredStudents(dedupedLocal);
                    setTotalStudents(dedupedLocal.length);
                } finally {
                    setIsSearching(false);
                }
            }, 400),
        []
    );

    const fetchSuggestions = useMemo(
        () =>
            debounce(async (query) => {
                const latestMemberType = memberTypeRef.current;
                const latestStudents = studentsDataRef.current;
                const trimmed = query.trim().toLowerCase();

                if (!trimmed || trimmed.length < 2) {
                    setSearchSuggestions([]);
                    setShowSuggestions(false);
                    return;
                }

                const localSuggestions = latestStudents
                    .filter(
                        (student) =>
                            (student.employee_name &&
                                student.employee_name.toLowerCase().includes(trimmed)) ||
                            (student.student_name &&
                                student.student_name.toLowerCase().includes(trimmed))
                    )
                    .slice(0, 10)
                    .map((student) => ({
                        id: student.student_id,
                        name:
                            student.student_name === "N/A"
                                ? "Unknown"
                                : student.student_name,
                        mobile:
                            student.mobile_number1 === "N/A"
                                ? "No Mobile"
                                : student.mobile_number1,
                        college:
                            student.clg_name === "N/A"
                                ? "Unknown College"
                                : student.clg_name,
                        employee: student.employee_name || "N/A",
                    }));

                try {
                    let apiEndpoint = SummaryApi.adminsearchbar.url;
                    let requestBody = { search_term: trimmed };

                    if (
                        latestMemberType === "Employee" ||
                        latestMemberType === "Digital Leads"
                    ) {
                        apiEndpoint = SummaryApi.employeesearchbar.url;
                        const encodedEmployeeId = sessionStorage.getItem(
                            "H7Z4L9VD2XK5JW8QRYT6"
                        );
                        if (encodedEmployeeId) {
                            requestBody = {
                                search_term: trimmed,
                                employee_id: atob(encodedEmployeeId),
                            };
                        }
                    }

                    const response = await fetch(apiEndpoint, {
                        method: "POST",
                        headers,
                        body: JSON.stringify(requestBody),
                    });

                    const data = await response.json();

                    if (data?.status && Array.isArray(data.data)) {
                        const serverSuggestions = data.data.map((student) => ({
                            id: student.student_id,
                            name:
                                student.student_name === "N/A"
                                    ? "Unknown"
                                    : student.student_name,
                            mobile:
                                student.mobile_number1 === "N/A"
                                    ? "No Mobile"
                                    : student.mobile_number1,
                            college:
                                student.clg_name === "N/A"
                                    ? "Unknown College"
                                    : student.clg_name,
                            employee: student.employee_name || "N/A",
                        }));

                        const seen = new Set();
                        const merged = [];
                        [...localSuggestions, ...serverSuggestions].forEach((item) => {
                            if (!seen.has(item.id)) {
                                seen.add(item.id);
                                merged.push(item);
                            }
                        });

                        setSearchSuggestions(merged.slice(0, 10));
                        setShowSuggestions(merged.length > 0);
                    } else {
                        setSearchSuggestions(localSuggestions);
                        setShowSuggestions(localSuggestions.length > 0);
                    }
                } catch (err) {
                    console.error("Suggestions error:", err);
                    setSearchSuggestions(localSuggestions);
                    setShowSuggestions(localSuggestions.length > 0);
                }
            }, 300),
        []
    );

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchQuery(val);

        if (val.trim().length >= 3) {
            debouncedSearch(val);
            fetchSuggestions(val);
        } else {
            debouncedSearch.cancel();
            fetchSuggestions.cancel();
            setShowSuggestions(false);
            setSearchSuggestions([]);

            setFilteredStudents(studentsDataRef.current);
            setTotalStudents(studentsDataRef.current.length);
            if (gotoPageRef.current) gotoPageRef.current(0);
            setLocalPageIndex(0);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchQuery(suggestion.name);
        setShowSuggestions(false);
        const result = filteredStudentsRef.current.filter(
            (s) => s.student_id === suggestion.id
        );
        setFilteredStudents(result.length ? result : []);
        setTotalStudents(result.length);
    };

    useEffect(() => {
        return () => {
            if (debouncedSearch.cancel) debouncedSearch.cancel();
            if (fetchSuggestions.cancel) fetchSuggestions.cancel();
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchInputRef.current &&
                !searchInputRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filterStudents = (filter, search, range, filterByDate) => {
        setLocalPageIndex(0);
        let filtered = [...studentsDataRef.current];

        if (filter === "Not Answered") {
            filtered = filtered.filter((student) => student?.call_status === filter);
        }

        if (filter !== "All" && filter !== "Not Answered") {
            filtered = filtered.filter((student) => student?.is_interested === filter);
        }

        if (range === "custom") {
            if (!fromDate || !toDate) return;
            const fromFormatted = new Date(fromDate);
            fromFormatted.setHours(0, 0, 0, 0);
            const toFormatted = new Date(toDate);
            toFormatted.setHours(23, 59, 59, 999);
            filtered = filtered.filter((student) => {
                const studentDate = new Date(student.created_date);
                return studentDate >= fromFormatted && studentDate <= toFormatted;
            });
        }

        if (filterByDate) {
            setFilterDate(filterByDate);
            const filterDateObj = new Date(filterByDate);
            filtered = filtered.filter((student) => {
                const studentDate = new Date(student.created_date);
                return studentDate.toDateString() === filterDateObj.toDateString();
            });
        }

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (student) =>
                    (student.student_name || "").toLowerCase().includes(searchLower) ||
                    (student.employee_name || "").toLowerCase().includes(searchLower) ||
                    (student.mobile_number1 || "").toLowerCase().includes(searchLower) ||
                    (student.clg_name || "").toLowerCase().includes(searchLower)
            );
        }

        setFilteredStudents(filtered);
        if (gotoPageRef.current) gotoPageRef.current(0);
    };

    const handleFilterChange = (filter) => {
        if (gotoPageRef.current) gotoPageRef.current(0);
        setLocalPageIndex(0);
        setSelectedFilter(filter);
        setActiveSearchTerm("");
        setSearchResults([]);
        setSearchQuery("");
        setShowSuggestions(false);
        filterStudents(filter, "", "custom", filterDate);
        setSearchDistrict("");
        setSearchCity("");
        setSearchCollege("");
    };

    const clearFilters = () => {
        if (window.history.pushState) {
            const cleanUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
            window.history.pushState({ path: cleanUrl }, "", cleanUrl);
        }

        const today = new Date();
        const firstDayLastMonth = new Date(
            today.getFullYear(),
            today.getMonth() - 1,
            1
        );
        setFromDate(firstDayLastMonth);
        setToDate(today);
        setActiveSearchTerm("");
        setSearchResults([]);
        setSearchQuery("");
        setSelectedFilter("All");
        setFilterDate(null);
        setSearchSuggestions([]);
        setShowSuggestions(false);
        setSearchDistrict("");
        setSearchCity("");
        setSearchCollege("");

        if (gotoPageRef.current) gotoPageRef.current(0);
        setLocalPageIndex(0);
        fetchStudents();
    };

    useEffect(() => {
        const init = async () => {
            const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
            const type = encodedMemberType ? atob(encodedMemberType) : null;

            setMemberType(type);
            memberTypeRef.current = type;

            if (fromDate && toDate) {
                await fetchStudents();
            }
        };

        if (fromDate && toDate) {
            init();
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        if (memberType && fromDate && toDate && studentsData.length === 0) {
            fetchStudents();
        }
    }, [memberType]);

    useEffect(() => {
        if (studentsData?.length) {
            const dates = studentsData.map(
                (student) => new Date(student.created_date)
            );
            const minDate = new Date(Math.min(...dates));
            setMinEnrolledDate(minDate);
            setMaxEnrolledDate(new Date());
        }
    }, [studentsData]);

    const ugCourses = [
        "CSE",
        "CSE(AI & ML)",
        "CSE(AI & DS)",
        "AERONAUTICAL ENGINERRING",
        "ECE",
    ];
    const pgCourses = [];
    const courses =
        formData.program_type === "UG"
            ? ugCourses
            : formData.program_type === "PG"
                ? pgCourses
                : [];

    const openModal = (student = null, mode = "view") => {
        const sanitizeStudentData = (data) => {
            const sanitizedData = { ...data };
            Object.keys(sanitizedData).forEach((key) => {
                if (sanitizedData[key] === "N/A" || sanitizedData[key] === "n/a") {
                    sanitizedData[key] = "";
                }
            });
            return sanitizedData;
        };

        const reasonsForOthers = [
            "Wrong Number",
            "Not Reachable",
            "Number Not Working",
            "Call Declined",
            "Busy",
            "Switched Off",
            "Incoming Calls Stopped",
        ];

        const payload = {
            employee_id: student?.employee_id,
            employee_student_id: student?.employee_student_id,
            student_name: student?.student_name,
            gender: student?.gender,
            father_name: student?.father_name,
            mobile_number1: student?.mobile_number1,
            mobile_number2: student?.mobile_number2 || "",
            email: student?.email || "",
            aadhar_number: student?.aadhar_number || "",
            state: student?.state,
            district: student?.district,
            city: student?.city,
            address: student?.address,
            pincode: student?.pincode || "",
            course: student?.course,
            clg_name: student?.clg_name,
            inter_percentage: student?.inter_percentage || "",
            program_type: student?.program_type,
            ug_pg_course: student?.ug_pg_course,
            call_status: student?.call_status || "",
            call_duration: student?.call_duration || "00:00:00.000000",
            remarks: student?.remarks || "",
            is_interested: reasonsForOthers.includes(student?.is_interested)
                ? "Others"
                : student?.is_interested,
            other_reasons: reasonsForOthers.includes(student?.is_interested)
                ? student?.is_interested
                : "",
            call_back_reason: student?.call_back_reason,
            prev_call_back_reason: student?.call_back_reason,
            is_jee: student?.is_jee,
            is_jee2: student?.is_jee2,
            lead_type: student?.lead_type || "",
            native_district: student?.native_district || "",
            native_address: student?.native_address || "",
            next_follow_up_date: student?.next_follow_up_date || "",
        };

        const sanitizedStudent = sanitizeStudentData(payload);

        if (mode === "edit" && sanitizedStudent.is_interested === "Interested") {
            setCanOnlySelectHigherInterest(true);
        } else {
            setCanOnlySelectHigherInterest(false);
        }

        const [timePart = "00:00:00"] = sanitizedStudent.call_duration.split(".");
        const [hours, minutes, seconds] = timePart.split(":");

        setFormData({
            ...sanitizedStudent,
            hours: hours?.padStart(2, "0") || "00",
            minutes: minutes?.padStart(2, "0") || "00",
            seconds: seconds?.padStart(2, "0") || "00",
        });

        if (mode === "edit") {
            setIsUpdate(true);
            setIsUpload(false);
            const coursesArray = sanitizedStudent.ug_pg_course
                ? sanitizedStudent.ug_pg_course
                    .split(",")
                    .map((course) => course.trim())
                : [];
            setSelectedCourses(coursesArray);
        } else if (mode === "add") {
            setFormData({
                student_name: "",
                gender: "Male",
                father_name: "",
                mobile_number1: "",
                mobile_number2: "",
                email: "",
                aadhar_number: "",
                state: "",
                district: "",
                city: "",
                address: "",
                pincode: "",
                course: "",
                clg_name: "",
                remarks: "",
                call_status: "",
                call_duration: "00:00:00.000000",
                program_type: "",
                ug_pg_course: "",
                is_interested: "",
                inter_percentage: "",
                is_jee: "",
                is_jee2: "",
                hours: "00",
                minutes: "00",
                seconds: "00",
                other_reasons: "",
                call_back_reason: "",
                lead_type: "",
                native_district: "",
                native_address: "",
                next_follow_up_date: "",
            });
            setIsUpdate(false);
            setIsUpload(true);
        } else {
            const coursesArray = sanitizedStudent.ug_pg_course
                ? sanitizedStudent.ug_pg_course
                    .split(",")
                    .map((course) => course.trim())
                : [];
            setSelectedCourses(coursesArray);
        }

        setSelectedStudent(student);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedStudent(null);
        setIsUpdate(false);
        setIsUpload(false);
        setSelectedCourses([]);
        setDropdownValue("");
        setCanOnlySelectHigherInterest(false);
    };

    const handleChange = (e) => {
        if (!e || !e.target) return;

        const { name, value, type, checked } = e.target;
        if (!name) return;

        const resolvedValue = type === "checkbox" ? checked : value;

        if (name === "call_status" && (value === "Not Answered" || value === "")) {
            setFormData((prev) => ({
                ...prev,
                is_interested: "",
                call_back_reason: "",
                other_reasons: "",
                [name]: resolvedValue,
            }));
        } else if (name === "is_interested") {
            setFormData((prev) => ({
                ...prev,
                [name]: resolvedValue,
                ...(value !== "Call Back" && { call_back_reason: "" }),
                ...(value !== "Others" && { other_reasons: "" }),
            }));
        } else if (name === "program_type" && (value === "PG" || value === "")) {
            setSelectedCourses([]);
            setDropdownValue("");
            setFormData((prev) => ({
                ...prev,
                [name]: resolvedValue,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: resolvedValue,
            }));
        }
    };

    const handleSelectChange = (e) => {
        const selectedCourse = e.target.value;
        if (selectedCourse && !selectedCourses.includes(selectedCourse)) {
            setSelectedCourses((prev) => [...prev, selectedCourse]);
        }
        setDropdownValue("");
    };

    const handleRemoveTag = (course) => {
        setSelectedCourses((prev) => prev.filter((c) => c !== course));
    };

    const validateForm = () => {
        const { call_status, is_interested, call_back_reason, other_reasons } =
            formData;
        if (call_status === "Contacted") {
            if (
                ![
                    "Not Interested",
                    "Interested",
                    "Most Interested",
                    "Call Back",
                    "Others",
                ].includes(is_interested)
            ) {
                toast.warning(
                    "Please select an option: Not Interested, Interested, Most Interested, Call Back, or Others."
                );
                return false;
            }
            if (is_interested === "Call Back" && !call_back_reason) {
                toast.warning("Please select a Call Back reason.");
                return false;
            }
            if (is_interested === "Others" && !other_reasons) {
                toast.warning("Please select an Other reason.");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoadingSubmit(true);
        if (!validateForm()) {
            setLoadingSubmit(false);
            return;
        }

        try {
            const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
            if (!encodedEmployeeId) throw new Error("Employee ID not found");
            const decodedEmployeeId = atob(encodedEmployeeId);
            const apiEndpoint = isUpdate
                ? SummaryApi.updateStudent.url
                : SummaryApi.uploadStudent.url;
            const method = isUpdate
                ? SummaryApi.updateStudent.method
                : SummaryApi.uploadStudent.method;

            const payloadData = {
                employee_student_id:
                    formData.employee_student_id || selectedStudent?.employee_student_id,
                student_name: formData.student_name,
                last_name: "",
                gender: formData.gender,
                father_name: formData.father_name,
                mobile_number1: formData.mobile_number1,
                mobile_number2: formData.mobile_number2 || "",
                email: formData.email || "",
                aadhar_number: formData.aadhar_number || "",
                state: formData.state,
                district: formData.district,
                city: formData.city,
                address: formData.address,
                pincode: formData.pincode || "",
                course: formData.course,
                clg_name: formData.clg_name,
                inter_percentage: formData.inter_percentage || "",
                program_type: formData.program_type,
                ug_pg_course: selectedCourses.join(", "),
                call_status: formData.call_status || "",
                call_duration: timer,
                remarks: formData.remarks || "",
                is_interested:
                    formData.is_interested === "Others"
                        ? formData.other_reasons
                        : formData.is_interested,
                is_jee:
                    formData.is_jee === "Jee Mains Results" ? "Jee Mains Results" : "",
                is_jee2:
                    formData.is_jee2 === "Jee Mains-2 Results"
                        ? "Jee Mains-2 Results"
                        : "",
                call_back_reason: formData.call_back_reason || "",
                follow_up_date: new Date().toISOString(),
                lead_type: formData.lead_type || selectedStudent?.lead_type || "",
                native_district: formData.native_district || "N/A",
                native_address: formData.native_address || "N/A",
            };

            if (formData.next_follow_up_date) {
                payloadData.next_follow_up_date = formData.next_follow_up_date;
            }

            const finalPayload = isUpdate
                ? {
                    ...payloadData,
                    student_id: selectedStudent.student_id,
                    employee_id: decodedEmployeeId,
                }
                : { ...payloadData, employee_id: decodedEmployeeId };

            const response = await fetch(apiEndpoint, {
                method: method,
                headers: headers,
                body: JSON.stringify(finalPayload),
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const result = await response.json();
            if (result.status) {
                toast.success(
                    isUpdate
                        ? "Student updated successfully!"
                        : "Student added successfully!"
                );
                setUpdatedData(payloadData);
                setUpdatedStudent(selectedStudent?.student_id);
                setStudentsData((prevStudentsData) =>
                    prevStudentsData.map((student) =>
                        student.student_id === finalPayload.student_id
                            ? { ...student, ...payloadData }
                            : student
                    )
                );
                setFilteredStudents((prevStudentsData) =>
                    prevStudentsData.map((student) =>
                        student.student_id === finalPayload.student_id
                            ? { ...student, ...payloadData }
                            : student
                    )
                );
                closeModal();
            } else {
                toast.error(result.message);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setLoadingSubmit(false);
        }
    };

    const downloadExcelReport = async () => {
        try {
            if (typeof setExporting === "function") setExporting(true);

            const currentDate = new Date();
            const fileDate = currentDate.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });

            const formattedFromDate = fromDate.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });

            const formattedToDate = toDate.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });

            const requiredFields = [
                "enrollment_code",
                "employee_name",
                "student_name",
                "district",
                "city",
                "clg_name",
                "address",
                "mobile_number1",
                "call_duration",
                "course",
                "created_date",
                "call_status",
                "other_reasons",
                "prevs_is_interested",
            ];

            const headersMap = {
                enrollment_code: "Enrollment Code",
                student_name: "Student Name",
                email: "Email",
                mobile_number1: "Mobile Number 1",
                mobile_number2: "Mobile Number 2",
                aadhar_number: "Aadhar Number",
                state: "State",
                district: "District",
                city: "City",
                address: "Address",
                pincode: "Pincode",
                course: "Course",
                clg_name: "College",
                created_date: "Enrolled Date",
                call_status: "Call Status",
                call_duration: "Call Duration",
                remarks: "Remarks",
                employee_name: "Employee Name",
                prevs_is_interested: "Previous Interest Status",
                is_interested: "Current Interest Status",
                call_back_reason: "Call Back Reason",
                other_reasons: "Other Reasons",
                program_type: "Program Type",
                ug_pg_course: "UG/PG Course",
                inter_percentage: "Intermediate Percentage",
                is_jee: "JEE Status",
                is_jee2: "JEE 2 Status",
            };

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Students Report");
            const totalCols = requiredFields.length;

            const titleRow = worksheet.addRow([
                "OAKYA IT SERVICES PVT LTD; STUDENTS REPORT",
            ]);
            titleRow.font = { bold: true, size: 18, underline: true };
            titleRow.alignment = { horizontal: "center", vertical: "center" };
            worksheet.mergeCells(1, 1, 1, totalCols);

            const dateRow = worksheet.addRow([
                `STUDENTS REPORT - ${formattedFromDate} to ${formattedToDate}`,
            ]);
            dateRow.font = { bold: true, italic: true, underline: true, size: 14 };
            dateRow.alignment = { horizontal: "center", vertical: "center" };
            worksheet.mergeCells(2, 1, 2, totalCols);

            worksheet.addRow([]);

            const excelHeaders = requiredFields.map(
                (field) => headersMap[field] || field
            );
            const headerRow = worksheet.addRow(excelHeaders);

            const thinBorder = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };

            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { horizontal: "center", vertical: "middle" };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "ADD8E6" },
                };
                cell.border = thinBorder;
            });

            filteredStudents.forEach((student) => {
                const rowValues = requiredFields.map((field) => student[field] ?? "");
                const dataRow = worksheet.addRow(rowValues);
                dataRow.eachCell((cell) => {
                    cell.alignment = { horizontal: "center", vertical: "middle" };
                    cell.border = thinBorder;
                });
            });

            worksheet.columns.forEach((column) => {
                let maxLength = 12;
                column.eachCell({ includeEmpty: false }, (cell) => {
                    const length = cell.value ? cell.value.toString().length : 0;
                    if (length > maxLength) {
                        maxLength = length;
                    }
                });
                column.width = Math.min(Math.max(maxLength + 3, 14), 35);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `Students_Report_${fileDate}.xlsx`);

            if (typeof toast !== "undefined") {
                toast.success("Students report exported successfully!");
            }
        } catch (err) {
            if (typeof toast !== "undefined") {
                toast.error(`Export failed: ${err.message}`);
            } else {
                console.error("Export failed:", err);
            }
        } finally {
            if (typeof setExporting === "function") setExporting(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date)) return "NA";
        const options = { day: "2-digit", month: "short", year: "numeric" };
        return date.toLocaleDateString("en-GB", options);
    };

    const renderStatusBadge = (status) => {
        if (!status) return <span className="text-slate-400 text-xs">NA</span>;
        let badgeStyle = "bg-slate-100 text-slate-700 border-slate-200";
        if (status === "Most Interested") {
            badgeStyle = "bg-[#F5F3FF] text-[#7C3AED] border-[#EDE9FE]";
        } else if (status === "Interested") {
            badgeStyle = "bg-[#ECFDF5] text-[#10B981] border-[#D1FAE5]";
        } else if (status === "Not Interested") {
            badgeStyle = "bg-[#FEF2F2] text-[#EF4444] border-[#FEE2E2]";
        } else if (status === "Call Back") {
            badgeStyle = "bg-[#FFFBEB] text-[#D97706] border-[#FEF3C7]";
        }
        return (
            <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeStyle}`}
            >
                {status}
            </span>
        );
    };

    const columns = useMemo(
        () => [
            { Header: "S.No", Cell: ({ row }) => row.index + 1 },
            ...(["Admin", "Manager"].includes(memberType)
                ? [{ Header: "Employee Name", accessor: "employee_name" }]
                : []),
            { Header: "Student Name", accessor: "student_name" },
            { Header: "District", accessor: "district" },
            { Header: "City", accessor: "city" },
            { Header: "College Name", accessor: "clg_name" },
            {
                Header: "Address",
                accessor: "address",
                Cell: ({ value }) => {
                    if (!value || value === "N/A" || value === "n/a") {
                        return <span className="text-gray-400">NA</span>;
                    }
                    return (
                        <div className="group relative max-w-[100px]">
                            <span className="block truncate text-gray-700">{value}</span>
                            <div className="absolute z-50 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 left-0 top-6 shadow-lg w-max max-w-[300px] whitespace-normal break-words pointer-events-none">
                                {value}
                            </div>
                        </div>
                    );
                },
            },
            { Header: "Mobile Number", accessor: "mobile_number1" },
            { Header: "Lead Type", accessor: "lead_type" },
            {
                Header: "Call Duration (HH:MM:SS)",
                accessor: "call_duration",
                Cell: ({ value }) => {
                    if (value === "n/a" || !value) return "N/A";
                    if (value.includes(":")) {
                        const [timePart] = value.split(".");
                        const [hours, minutes, seconds] = timePart.split(":");
                        return `${hours.padStart(2, "0")}:${minutes.padStart(
                            2,
                            "0"
                        )}:${seconds.padStart(2, "0")}`;
                    }
                    return value;
                },
            },
            { Header: "Course", accessor: "course" },
            {
                Header: "Enrolled Date",
                accessor: "enroll_date",
                Cell: ({ row }) => (
                    <span>{formatDate(row.original.created_date)}</span>
                ),
            },
            {
                Header: "Follow Up Date",
                accessor: "follow_up",
                Cell: ({ row }) => (
                    <span>{formatDate(row.original.follow_up_date)}</span>
                ),
            },
            {
                Header: "Next Follow Up Date",
                accessor: "next_follow_up",
                Cell: ({ row }) => (
                    <span>{formatDate(row.original.next_follow_up_date)}</span>
                ),
            },
            // {
            //     Header: "Previous Status",
            //     accessor: "prevs_is_interested_status",
            //     Cell: ({ row }) => renderStatusBadge(row.original.prevs_is_interested),
            // },
            {
                Header: "Current Status",
                accessor: "current_is_interested_status",
                Cell: ({ row }) => {
                    const { is_interested, call_status } = row.original;
                    const normalizedInterest = (is_interested || "").trim().toLowerCase();

                    const status =
                        !normalizedInterest || normalizedInterest === "n/a" || normalizedInterest === "na"
                            ? call_status
                            : is_interested;

                    return renderStatusBadge(status);
                },
            },
            ...(["Employee", "Team Lead"].includes(memberType)
                ? [
                    {
                        Header: "Edit",
                        accessor: "edit",
                        Cell: ({ row }) => {
                            return (
                                <button
                                    className="bg-[#10B981] hover:bg-[#059669] text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors shadow-sm inline-flex items-center"
                                    onClick={() => openModal(row.original, "edit")}
                                >
                                    <AiOutlineEdit className="mr-1" size={14} />
                                    Edit
                                </button>
                            );
                        },
                    },
                ]
                : []),
            {
                Header: "View More",
                accessor: "view",
                Cell: ({ row }) => (
                    <button
                        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors shadow-sm inline-flex items-center whitespace-nowrap"
                        onClick={() => openModal(row.original, "view")}
                    >
                        <AiOutlineEye className="mr-1" size={14} />
                        View Details
                    </button>
                ),
            },
        ],
        [memberType]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        state: { pageIndex, pageSize },
        page,
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        pageCount,
    } = useTable(
        {
            columns,
            data: filteredStudents,
            initialState: { pageIndex: localPageIndex },
        },
        usePagination
    );

    useEffect(() => {
        gotoPageRef.current = gotoPage;
    }, [gotoPage]);

    useEffect(() => {
        setLocalPageIndex(pageIndex);
    }, [pageIndex]);

    return (
        <div className="min-h-screen space-y-4 text-slate-800">
            {pageLoading ? (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 flex justify-center items-center">
                    <div className="bg-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3">
                        <div
                            className="animate-spin rounded-full h-6 w-6 border-b-2"
                            style={{ borderColor: THEME.primary }}
                        ></div>
                        <span className="text-slate-700 text-sm font-semibold">
                            Fetching Data...
                        </span>
                    </div>
                </div>
            ) : (
                <>
                    {loadingSubmit && (
                        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 flex justify-center items-center">
                            <div className="bg-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3">
                                <div
                                    className="animate-spin rounded-full h-6 w-6 border-b-2"
                                    style={{ borderColor: THEME.primary }}
                                ></div>
                                <span className="text-slate-700 text-sm font-semibold">
                                    Submitting...
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 shadow-sm flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                        <div className="flex items-center gap-3.5">
                            <div
                                className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-sm shrink-0"
                                style={{ backgroundColor: THEME.primary }}
                            >
                                <FaUserGraduate size={19} />
                            </div>
                            <div>
                                <h1
                                    className="text-lg font-bold tracking-tight"
                                    style={{ color: THEME.primary }}
                                >
                                    Total Enrolled Students
                                </h1>
                                <p className="text-xs text-slate-500">
                                    Manage, filter, and review student enrollment records and call statuses.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-between lg:justify-end">
                            <div className="bg-[#F0F5FF] border border-[#DBEAFE] px-3.5 py-1.5 rounded-lg text-xs font-medium text-slate-700 flex items-center gap-1.5">
                                <span>Records Loaded:</span>
                                <span className="font-bold text-[#2563EB]">
                                    {studentsData.length.toLocaleString()}
                                </span>
                                <span className="text-slate-400">of</span>
                                <span className="font-bold text-[#10B981]">
                                    {totalStudents.toLocaleString()}
                                </span>
                            </div>

                            <div className="flex items-center gap-2.5">
                                <button
                                    onClick={() => setSearchModalIsOpen(true)}
                                    style={{ backgroundColor: THEME.primary }}
                                    className="hover:opacity-90 text-white text-xs font-medium px-4 py-2 rounded-lg shadow-sm transition-opacity flex items-center gap-1.5"
                                >
                                    <FaSlidersH size={12} />
                                    Advanced Search
                                </button>

                                {["Admin"].includes(memberType) && (
                                    <button
                                        className={`flex items-center gap-1.5 bg-[#10B981] hover:bg-[#059669] text-white text-xs font-medium px-4 py-2 rounded-lg shadow-sm transition-colors ${pageLoading ? "opacity-60 cursor-not-allowed" : ""
                                            }`}
                                        onClick={downloadExcelReport}
                                        disabled={pageLoading}
                                    >
                                        <FaFileExcel size={14} />
                                        Export Excel
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-3.5">
                        <div className="flex flex-col xl:flex-row justify-between items-stretch xl:items-end gap-4">
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                                    Call Status Filter
                                </label>
                                <div className="inline-flex bg-[#F8FAFC] p-1 rounded-lg border border-slate-200 gap-1">
                                    {[
                                        "All",
                                        "Not Answered",
                                        "Interested",
                                        "Most Interested",
                                        "Not Interested",
                                    ].map((statusTab) => {
                                        const isActive = selectedFilter === statusTab;
                                        return (
                                            <button
                                                key={statusTab}
                                                onClick={() => handleFilterChange(statusTab)}
                                                style={
                                                    isActive
                                                        ? { backgroundColor: THEME.primary }
                                                        : {}
                                                }
                                                className={`py-1.5 px-4 rounded-md text-xs font-semibold transition-all ${isActive
                                                    ? "text-white shadow-sm"
                                                    : "text-slate-600 hover:text-slate-900 hover:bg-white"
                                                    }`}
                                            >
                                                {statusTab}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div
                                className="flex-1 max-w-md relative"
                                ref={searchInputRef}
                            >
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                                    Quick Search
                                </label>
                                <div className="relative">
                                    <AiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        placeholder="Search by student name, employee name, mobile, or college..."
                                        className="py-1.5 pl-9 pr-4 border border-slate-200 bg-[#F8FAFC] focus:bg-white rounded-lg w-full text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#172C57] transition-all"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap items-end gap-2.5">
                                {["Admin", "Manager", "Employee", "Digital Leads"].includes(
                                    memberType
                                ) && (
                                        <>
                                            <div>
                                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                                                    From Date
                                                </label>
                                                <DatePicker
                                                    selected={fromDate}
                                                    onChange={(date) => {
                                                        setFromDate(date);
                                                        setActiveSearchTerm("");
                                                        setSearchResults([]);
                                                        setSearchQuery("");
                                                        setShowSuggestions(false);
                                                        if (gotoPageRef.current) gotoPageRef.current(0);
                                                        setLocalPageIndex(0);
                                                        fetchStudents();
                                                    }}
                                                    selectsStart
                                                    startDate={fromDate}
                                                    endDate={toDate}
                                                    maxDate={toDate || maxEnrolledDate}
                                                    dateFormat="dd-MM-yyyy"
                                                    placeholderText="From"
                                                    className="py-1.5 px-3 border border-slate-200 bg-[#F8FAFC] focus:bg-white rounded-lg text-xs text-slate-700 w-32 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                                                    To Date
                                                </label>
                                                <DatePicker
                                                    selected={toDate}
                                                    onChange={(date) => {
                                                        setToDate(date);
                                                        setActiveSearchTerm("");
                                                        setSearchResults([]);
                                                        setSearchQuery("");
                                                        setShowSuggestions(false);
                                                        if (gotoPageRef.current) gotoPageRef.current(0);
                                                        setLocalPageIndex(0);
                                                        fetchStudents();
                                                    }}
                                                    selectsEnd
                                                    startDate={fromDate}
                                                    endDate={toDate}
                                                    minDate={fromDate}
                                                    maxDate={maxEnrolledDate}
                                                    dateFormat="dd-MM-yyyy"
                                                    placeholderText="To"
                                                    className="py-1.5 px-3 border border-slate-200 bg-[#F8FAFC] focus:bg-white rounded-lg text-xs text-slate-700 w-32 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                            </div>
                                            <button
                                                onClick={clearFilters}
                                                className="px-3.5 py-1.5 bg-[#FEF2F2] hover:bg-[#FEE2E2] text-[#EF4444] border border-[#FECACA] rounded-lg text-xs font-semibold transition-colors"
                                            >
                                                Clear Filters
                                            </button>
                                        </>
                                    )}

                                {memberType === "Consultant" && (
                                    <div>
                                        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                                            Filter Date
                                        </label>
                                        <DatePicker
                                            selected={filterDate}
                                            onChange={(date) => {
                                                setFilterDate(date);
                                                filterStudents(
                                                    selectedFilter,
                                                    searchQuery,
                                                    "custom",
                                                    date
                                                );
                                            }}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="Select Date"
                                            className="py-1.5 px-3 border border-slate-200 bg-[#F8FAFC] focus:bg-white rounded-lg text-xs text-slate-700 w-36 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative">
                        <EnrolledStudentsTable
                            getTableProps={getTableProps}
                            getTableBodyProps={getTableBodyProps}
                            headerGroups={headerGroups}
                            page={page}
                            prepareRow={prepareRow}
                            pageIndex={pageIndex}
                            pageCount={pageCount}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            gotoPage={gotoPage}
                            nextPage={nextPage}
                            previousPage={previousPage}
                            setPageSize={setPageSize}
                            pageSize={pageSize}
                            filteredStudentsLength={filteredStudents.length}
                            updatedStudent={updatedStudent}
                            updatedData={updatedData}
                        />

                        {(tableLoading || isSearching) && (
                            <div className="absolute inset-0 bg-white/75 backdrop-blur-[1px] flex items-center justify-center z-10">
                                <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-md border border-slate-200">
                                    <div
                                        className="animate-spin rounded-full h-5 w-5 border-b-2"
                                        style={{ borderColor: THEME.primary }}
                                    ></div>
                                    <span className="text-slate-700 text-xs font-semibold">
                                        {isSearching ? "Searching records..." : "Loading..."}
                                    </span>
                                </div>
                            </div>
                        )}

                        {filteredStudents.length === 0 && !pageLoading && !isSearching && (
                            <div className="text-center py-10 text-slate-400 text-xs">
                                No students found. Try adjusting your filters or search criteria.
                            </div>
                        )}
                    </div>

                    <EnrolledStudentsModal
                        isOpen={modalIsOpen}
                        onRequestClose={closeModal}
                        formData={formData}
                        handleChange={handleChange}
                        handleSelectChange={handleSelectChange}
                        handleRemoveTag={handleRemoveTag}
                        handleSubmit={handleSubmit}
                        selectedCourses={selectedCourses}
                        dropdownValue={dropdownValue}
                        courses={courses}
                        districts={districts}
                        isUpdate={isUpdate}
                        isUpload={isUpload}
                        timer={timer}
                        selectedStudent={selectedStudent}
                        canOnlySelectHigherInterest={canOnlySelectHigherInterest}
                        loadingSubmit={loadingSubmit}
                    />

                    <Modal
                        isOpen={searchModalIsOpen}
                        onRequestClose={() => setSearchModalIsOpen(false)}
                        contentLabel="Search Filters"
                        className="mx-auto w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden outline-none"
                        overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    >
                        <div
                            style={{ backgroundColor: THEME.primary }}
                            className="px-5 py-4 flex justify-between items-center text-white"
                        >
                            <div className="flex items-center gap-2.5">
                                <FaFilter size={14} />
                                <h2 className="text-sm font-bold tracking-wide">
                                    Advanced Search Filters
                                </h2>
                            </div>
                            <button
                                onClick={() => setSearchModalIsOpen(false)}
                                className="text-white/80 hover:text-white w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            >
                                <AiOutlineClose className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="p-5 space-y-4 bg-[#F8FAFC]">
                            <div className="flex flex-col">
                                <label className="mb-1 text-xs font-semibold text-slate-700">
                                    District
                                </label>
                                <select
                                    className="p-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                                    value={searchDistrict}
                                    onChange={(e) => {
                                        setSearchDistrict(e.target.value);
                                        setSearchCity("");
                                        setSearchCollege("");
                                    }}
                                >
                                    <option value="">Select District</option>
                                    {Array.from(
                                        new Set(
                                            studentsData
                                                .map((student) => student.district?.trim())
                                                .filter(Boolean)
                                        )
                                    )
                                        .sort((a, b) =>
                                            a.localeCompare(b, undefined, { sensitivity: "base" })
                                        )
                                        .map((district) => (
                                            <option key={district} value={district}>
                                                {district}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="flex flex-col">
                                <label className="mb-1 text-xs font-semibold text-slate-700">
                                    City
                                </label>
                                <select
                                    className="p-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] disabled:bg-slate-100 disabled:text-slate-400"
                                    value={searchCity}
                                    onChange={(e) => {
                                        setSearchCity(e.target.value);
                                        setSearchCollege("");
                                    }}
                                    disabled={!searchDistrict}
                                >
                                    <option value="">
                                        {searchDistrict ? "Select City" : "First select District"}
                                    </option>
                                    {Array.from(
                                        new Set(
                                            studentsData
                                                .filter(
                                                    (student) =>
                                                        !searchDistrict ||
                                                        student.district?.trim().toLowerCase() ===
                                                        searchDistrict.trim().toLowerCase()
                                                )
                                                .map((student) => student.city?.trim())
                                                .filter(Boolean)
                                        )
                                    )
                                        .sort((a, b) =>
                                            a.localeCompare(b, undefined, { sensitivity: "base" })
                                        )
                                        .map((city) => (
                                            <option key={city} value={city}>
                                                {city}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="flex flex-col">
                                <label className="mb-1 text-xs font-semibold text-slate-700">
                                    College Name
                                </label>
                                <select
                                    className="p-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] disabled:bg-slate-100 disabled:text-slate-400"
                                    value={searchCollege}
                                    onChange={(e) => setSearchCollege(e.target.value)}
                                    disabled={!searchCity}
                                >
                                    <option value="">
                                        {searchCity ? "Select College" : "First select City"}
                                    </option>
                                    {Array.from(
                                        new Set(
                                            studentsData
                                                .filter(
                                                    (student) =>
                                                        (!searchDistrict ||
                                                            student.district?.trim().toLowerCase() ===
                                                            searchDistrict.trim().toLowerCase()) &&
                                                        (!searchCity ||
                                                            student.city?.trim().toLowerCase() ===
                                                            searchCity.trim().toLowerCase())
                                                )
                                                .map((student) => student.clg_name?.trim())
                                                .filter(Boolean)
                                        )
                                    )
                                        .sort((a, b) =>
                                            a.localeCompare(b, undefined, { sensitivity: "base" })
                                        )
                                        .map((college) => (
                                            <option key={college} value={college}>
                                                {college}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button
                                    onClick={() => {
                                        setSearchDistrict("");
                                        setSearchCity("");
                                        setSearchCollege("");
                                        setActiveSearchTerm("");
                                        setSearchResults([]);
                                        setSearchQuery("");
                                        setShowSuggestions(false);
                                        setFilteredStudents(studentsData);
                                        setTotalStudents(studentsData.length);
                                        if (gotoPageRef.current) gotoPageRef.current(0);
                                        setLocalPageIndex(0);
                                    }}
                                    className="px-4 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                    Reset
                                </button>

                                <button
                                    onClick={() => {
                                        setActiveSearchTerm("");
                                        setSearchResults([]);
                                        setSearchQuery("");
                                        setShowSuggestions(false);

                                        const filtered = studentsData.filter((student) => {
                                            const districtMatch =
                                                !searchDistrict ||
                                                student.district
                                                    ?.trim()
                                                    .toLowerCase() === searchDistrict.trim().toLowerCase();
                                            const cityMatch =
                                                !searchCity ||
                                                student.city
                                                    ?.trim()
                                                    .toLowerCase() === searchCity.trim().toLowerCase();
                                            const collegeMatch =
                                                !searchCollege ||
                                                student.clg_name
                                                    ?.trim()
                                                    .toLowerCase() === searchCollege.trim().toLowerCase();
                                            return districtMatch && cityMatch && collegeMatch;
                                        });

                                        setFilteredStudents(filtered);
                                        setTotalStudents(filtered.length);
                                        if (gotoPageRef.current) gotoPageRef.current(0);
                                        setLocalPageIndex(0);
                                        setSearchModalIsOpen(false);
                                    }}
                                    style={{ backgroundColor: THEME.primary }}
                                    className="px-4 py-1.5 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-sm"
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    </Modal>
                </>
            )}
        </div>
    );
};

function debounce(func, wait) {
    let timeout;
    const debounced = function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            func.apply(context, args);
        }, wait);
    };
    debounced.cancel = () => {
        clearTimeout(timeout);
    };
    return debounced;
}

export default EnrolledStudents;
